import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { outboxApi } from '../../api/outbox'
import { RefusedError } from '../../api/client'
import { Correct } from '../../components/Correct'
import { useSpoke } from '../../hooks/useSpoke'
import type { OutboundView } from '../../types/outbox'
import { ComposeCard, ComposeStart } from './ComposeParts'

// COMPOSE (owner, 2026-09-17): "draft emails he can send out (or we can send from his mailbox with his
// manual approval) after he tweaks the language". One person, one draft, in their voice, written to a
// play from the playbook. Edits save on blur and teach the voice. Nothing is sent by this panel except on
// a click that names ONE message: Copy and Open in Outlook hand the words to the person's own tooling;
// Approve records the play and sends nothing; Send from my mailbox is a second click behind a separate
// grant and carries the hash of the text on screen. There is no send-all here and there will not be one.

export function Compose({ personId, hasAddress, modelConfigured }: { personId: string; hasAddress: boolean; modelConfigured: boolean }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [view, setView] = useState<OutboundView | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [copied, setCopied] = useState(false)
  useEffect(() => { setView(null); setSubject(''); setBody(''); setCopied(false) }, [personId])

  const land = (v: OutboundView) => {
    setView(v); setSubject(v.row.subject); setBody(v.row.finalText)
    void qc.invalidateQueries({ queryKey: ['outbox'] })
    void qc.invalidateQueries({ queryKey: ['applications'] })
  }
  const message = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : (e as Error)?.message ?? 'Something went wrong.')

  const compose = useMutation({ mutationFn: () => outboxApi.compose(spoke, personId), onSuccess: land })
  const save = useMutation({ mutationFn: () => outboxApi.edit(spoke, view!.row.outboundId, subject, body), onSuccess: (v) => setView(v) })
  const approve = useMutation({ mutationFn: async () => { await outboxApi.edit(spoke, view!.row.outboundId, subject, body); return outboxApi.approve(spoke, view!.row.outboundId) }, onSuccess: land })
  const discard = useMutation({ mutationFn: () => outboxApi.discard(spoke, view!.row.outboundId), onSuccess: land })
  const sentByHand = useMutation({ mutationFn: async (via: 'copy' | 'mailto') => { await outboxApi.edit(spoke, view!.row.outboundId, subject, body); return outboxApi.sentByHand(spoke, view!.row.outboundId, via) }, onSuccess: land })
  // the hash is the one the API handed back for the text it holds; a save moved it, so save first and
  // send what was saved — never what a stale view remembers
  const send = useMutation({
    mutationFn: async () => {
      const saved = await outboxApi.edit(spoke, view!.row.outboundId, subject, body)
      return outboxApi.send(spoke, saved.row.outboundId, saved.finalTextHash)
    },
    onSuccess: land,
  })

  const busy = compose.isPending || save.isPending || approve.isPending || discard.isPending || sentByHand.isPending || send.isPending
  const error = [compose, save, approve, discard, sentByHand, send].find((m) => m.isError)?.error

  const onBlur = () => {
    if (!view || (view.row.status !== 'drafted' && view.row.status !== 'approved')) return
    if (subject === view.row.subject && body === view.row.finalText) return
    save.mutate()
  }
  const onCopy = () => {
    const text = subject ? `Subject: ${subject}\n\n${body}` : body
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined
    if (clip) void clip.writeText(text).catch(() => undefined)
    setCopied(true)
    onBlur()
  }

  if (!view) {
    return <ComposeStart onStart={() => compose.mutate()} busy={compose.isPending} modelConfigured={modelConfigured} hasAddress={hasAddress} error={compose.isError ? message(compose.error) : null} />
  }
  const correct = view.row.applicationId
    ? <Correct kind="play" entityId={view.row.applicationId} entityLabel={view.tacticName ?? 'this play'} currentValue={view.row.tacticId ? `tactic:${view.row.tacticId}` : null} compact />
    : undefined
  return (
    <ComposeCard
      view={view} subject={subject} body={body}
      onSubject={setSubject} onBody={setBody} onBlur={onBlur}
      onCopy={onCopy} onApprove={() => approve.mutate()} onSend={() => send.mutate()} onDiscard={() => discard.mutate()}
      onSentByHand={(via) => sentByHand.mutate(via)}
      correct={correct} busy={busy} saving={save.isPending} copied={copied} error={error ? message(error) : null}
    />
  )
}
